import { DebtResponseType } from "constants/types/debt";
import {
  ReceiptBulkCreateBody,
  ReceiptBulkCreateItemBody,
} from "constants/types/receipt";
import { formatCurrency } from "utils/helpers/format";
import * as yup from "yup";
import {
  BankAccountFormValues,
  PaymentMethod,
  emptyBankAccount,
} from "../BankAccountFields/config";

export const MAX_BULK_ITEMS = 100;

export const generateClientId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ── Step 1: số tiền cần thu theo từng công nợ ───────────────────────────────

export interface BulkReceiptItemFormValues {
  clientId: string;
  debtId: string;
  saleOrderId: string;
  debtCode: string;
  partnerName: string;
  saleOrderCode: string;
  /** Số dư còn lại của công nợ, chụp tại thời điểm mở modal — chỉ đọc. */
  remaining: number;
  amount: number | "";
  paymentDate: Date | null;
  notes: string;
}

export interface BulkReceiptItemsFormValues {
  items: BulkReceiptItemFormValues[];
}

export const emptyBulkItem = (
  debt: DebtResponseType,
): BulkReceiptItemFormValues => ({
  clientId: generateClientId(),
  debtId: debt.id,
  saleOrderId: debt.saleOrderId,
  debtCode: debt.code,
  partnerName: debt.partner.name,
  saleOrderCode: debt.saleOrder.code,
  remaining: debt.totalAmount - debt.paidAmount,
  amount: debt.totalAmount - debt.paidAmount,
  paymentDate: new Date(),
  notes: "",
});

export const initialBulkItemsValues = (
  debts: DebtResponseType[],
): BulkReceiptItemsFormValues => ({
  items: debts.map(emptyBulkItem),
});

const bulkItemSchema = yup.object({
  amount: yup
    .number()
    .typeError("Vui lòng nhập số tiền")
    .required("Vui lòng nhập số tiền")
    .min(0, "Số tiền không được âm")
    // Đơn 0 đồng (remaining = 0) buộc phải cho nhập 0, các đơn còn dư phải > 0.
    .test("required-positive", "Số tiền phải lớn hơn 0", function (value) {
      const remaining = this.parent.remaining as number;
      if (remaining === 0) return true;
      return (value ?? 0) > 0;
    }),
  paymentDate: yup
    .date()
    .typeError("Vui lòng chọn ngày thu")
    .required("Vui lòng chọn ngày thu"),
  notes: yup.string().optional(),
});

export const bulkItemsValidationSchema = yup.object({
  items: yup
    .array()
    .of(bulkItemSchema)
    .min(1, "Cần ít nhất một phiếu thu")
    .max(MAX_BULK_ITEMS, `Không được tạo quá ${MAX_BULK_ITEMS} phiếu thu trong một lần`),
});

/**
 * Số dư còn lại hiển thị trên từng dòng, tính lũy kế theo saleOrderId: dòng
 * sau trừ tiếp vào phần còn lại của dòng trước (theo thứ tự trong mảng),
 * thay vì mỗi dòng đều hiển thị lại nguyên số dư gốc của công nợ.
 */
export const computeRunningRemaining = (
  items: BulkReceiptItemFormValues[],
): number[] => {
  const runningBySaleOrder: Record<string, number> = {};
  return items.map((item) => {
    const before = runningBySaleOrder[item.saleOrderId] ?? item.remaining;
    const after = before - (Number(item.amount) || 0);
    runningBySaleOrder[item.saleOrderId] = after;
    return after;
  });
};

/**
 * Yup chỉ validate được từng dòng độc lập. Tổng amount theo từng saleOrderId
 * (có thể trải trên nhiều dòng do "Thêm đợt thu") phải được kiểm tra riêng,
 * gắn lỗi vào field amount của các dòng liên quan.
 */
export const validateBulkGroupSums = (values: BulkReceiptItemsFormValues) => {
  const groups = new Map<
    string,
    { indices: number[]; remaining: number; debtCode: string }
  >();

  values.items.forEach((item, index) => {
    const group = groups.get(item.saleOrderId) ?? {
      indices: [],
      remaining: item.remaining,
      debtCode: item.debtCode,
    };
    group.indices.push(index);
    groups.set(item.saleOrderId, group);
  });

  const itemErrors: Array<{ amount: string } | undefined> = [];

  groups.forEach((group) => {
    const sum = group.indices.reduce(
      (total, index) => total + (Number(values.items[index].amount) || 0),
      0,
    );
    if (sum > group.remaining) {
      const message = `Tổng số tiền thu cho công nợ ${group.debtCode} (${formatCurrency(sum)}) vượt quá số dư còn lại (${formatCurrency(group.remaining)})`;
      group.indices.forEach((index) => {
        itemErrors[index] = { amount: message };
      });
    }
  });

  return Object.keys(itemErrors).length > 0 ? { items: itemErrors } : {};
};

// ── Step 2: phương thức thanh toán chung cho toàn bộ lô phiếu thu ───────────

export interface BulkReceiptPaymentFormValues {
  paymentMethod: PaymentMethod;
  bankAccount: BankAccountFormValues;
}

export const initialBulkPaymentValues = (): BulkReceiptPaymentFormValues => ({
  paymentMethod: "bank",
  bankAccount: emptyBankAccount(),
});

export const bulkPaymentValidationSchema = yup.object({
  bankAccount: yup.object().when("paymentMethod", {
    is: "bank",
    then: (schema) =>
      schema.shape({
        id: yup.string(),
        bankName: yup.string().required("Vui lòng nhập tên ngân hàng"),
        accountNumber: yup.string().required("Vui lòng nhập số tài khoản"),
        accountHolder: yup.string().required("Vui lòng nhập chủ tài khoản"),
      }),
    otherwise: (schema) => schema.optional(),
  }),
});

// ── Ghép items (step 1) với payment method (step 2) thành payload gửi BE ───

export const toBulkCreateBody = (
  items: BulkReceiptItemFormValues[],
  payment: BulkReceiptPaymentFormValues,
): ReceiptBulkCreateBody => ({
  items: items.map(
    (item): ReceiptBulkCreateItemBody => ({
      saleOrderId: item.saleOrderId,
      amount: Number(item.amount),
      paymentDate: Math.floor((item.paymentDate as Date).getTime() / 1000),
      ...(payment.paymentMethod === "bank" && {
        bankAccount: payment.bankAccount.id
          ? { id: payment.bankAccount.id }
          : {
              bankName: payment.bankAccount.bankName,
              accountNumber: payment.bankAccount.accountNumber,
              accountHolder: payment.bankAccount.accountHolder,
              isActive: payment.bankAccount.isActive,
            },
      }),
      ...(item.notes ? { notes: item.notes } : {}),
    }),
  ),
});
